/**
 * Study group operations — CRUD for groups and memory passage management.
 *
 * Student/teacher member operations are in study-member-ops.ts.
 */

import type { StudyGroup, BibleLocation } from '@skjv/common';
import { compareLocations } from '@skjv/common';

/** Helpers that group operations need from the parent StudyState */
export interface GroupStoreDeps {
  studyGroups: StudyGroup[];
  generateId: () => string;
  now: () => string;
  saveStudyGroups: () => void;
}

// ============================================================
// Study Group CRUD
// ============================================================

/** Add a new study group */
export function addStudyGroup(
  deps: GroupStoreDeps,
  name: string,
  description?: string,
  leaderCount: number = 1,
): string {
  const id = deps.generateId();
  const newGroup: StudyGroup = {
    id,
    name,
    description,
    leaderCount,
    teachers: [],
    students: [],
    updatedAt: deps.now(),
  };
  deps.studyGroups = [...deps.studyGroups, newGroup];
  deps.saveStudyGroups();
  return id;
}

/** Update a study group */
export function updateStudyGroup(
  deps: GroupStoreDeps,
  id: string,
  updates: Partial<Omit<StudyGroup, 'id' | 'students'>>,
): void {
  deps.studyGroups = deps.studyGroups.map((g) =>
    g.id === id ? { ...g, ...updates, updatedAt: deps.now() } : g,
  );
  deps.saveStudyGroups();
}

/** Remove a study group */
export function removeStudyGroup(deps: GroupStoreDeps, id: string): void {
  deps.studyGroups = deps.studyGroups.filter((g) => g.id !== id);
  deps.saveStudyGroups();
}

/** Get a study group by ID */
export function getStudyGroup(studyGroups: StudyGroup[], id: string): StudyGroup | undefined {
  return studyGroups.find((g) => g.id === id);
}

// ============================================================
// Memory Passage (on study groups)
// ============================================================

/** Set memory passage start location - clears the ending */
export function setStudyGroupMemoryStart(
  deps: GroupStoreDeps,
  groupId: string,
  location: BibleLocation | null,
): void {
  deps.studyGroups = deps.studyGroups.map((g) =>
    g.id === groupId
      ? {
          ...g,
          memoryStartingLocation: location || undefined,
          memoryEndingLocation: undefined,
          updatedAt: deps.now(),
        }
      : g,
  );
  deps.saveStudyGroups();
}

/** Set memory passage end location - only updates if it extends the range */
export function setStudyGroupMemoryEnd(
  deps: GroupStoreDeps,
  groupId: string,
  location: BibleLocation | null,
): void {
  if (!location) {
    deps.studyGroups = deps.studyGroups.map((g) =>
      g.id === groupId ? { ...g, memoryEndingLocation: undefined, updatedAt: deps.now() } : g,
    );
    deps.saveStudyGroups();
    return;
  }

  const group = deps.studyGroups.find((g) => g.id === groupId);
  if (!group?.memoryStartingLocation) return;

  const currentEnd = group.memoryEndingLocation;
  if (!currentEnd || compareLocations(location, currentEnd) > 0) {
    deps.studyGroups = deps.studyGroups.map((g) =>
      g.id === groupId ? { ...g, memoryEndingLocation: location, updatedAt: deps.now() } : g,
    );
    deps.saveStudyGroups();
  }
}
